/* eslint-disable jsx-a11y/anchor-is-valid */
/**
 * React-based video player that defaults to YouTube. Falls back to an HTML
 * player if YouTube is unavailable or undesired.
 *
 * NOTE: This file had a ton of UI components stripped from it, including:
 *
 *   - Speed/quality/volume control
 *   - Subtitles
 *   - Playback control
 *   - …and more.
 *
 * The Git history is rich. If you're looking to implement new UI controls,
 * look at the commit that this comment comes from.
 *
 * TODO(riley): Add a fixture. Deferring for now because some props (iframeId,
 *              etc.) don't make much sense in the context of a fixture.
 *
 * WARNING(kevinb): `model` is typed as `any`.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import classNames from "classnames";
import gql from "graphql-tag";
import {compose} from "mu-lambda";
import * as React from "react";
import {findDOMNode} from "react-dom";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {PermissionsInfo} from "@khan/shared-core";
import {mediaQueries, topBarHeightDesktop} from "@khan/shared-styles";
import {reload} from "@khan/urls";

import ClarificationNotifications from "#clarifications-package/official.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {getKaid} from "#data-access/profile-utils.ts";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedSpinner from "#shared-components-package/deprecated-spinner.tsx";
import supports from "#shared-package/supports.ts";
import type {Action} from "#user-info-store-package/user-info-reducer.ts";
import userInfoStore from "#user-info-store-package/user-info-store.ts";
import {formatTimeDisplay} from "#utils/time-package/main.ts";

import {VideoPlayerState} from "../constants.ts";
import type {
    PlaybackRate,
    VideoPlayerModelType,
    VideoPlayerStateType,
} from "../types.ts";
import {
    addFullscreenChangeHandler,
    exitFullscreen,
    isInApp,
    isUnstartedOrCued,
    isYouTubeBlocked,
    markFallbackPlayerOptional,
    markFallbackPlayerRequired,
    removeFullscreenChangeHandler,
    requireYouTubeAPI,
    shouldUseFallbackPlayer,
} from "../util.ts";

import HtmlPlayerWrapper from "./html-player-wrapper.tsx";
import {
    updateMuteVideos,
    updatePlaybackRate,
    updateShowCaptions,
} from "./video-player-settings-mutations.ts";
import YouTubePlayer, {type ErrorOptions} from "./youtube-player.tsx";

const playIcon = `M1.6,9.9C1.5,10,1.3,10,1.2,10C1.1,10,1,10,0.9,9.9C0.7,9.8,0.6,9.6,
    0.6,9.4V0.6c0-0.2,0.2-0.4,0.4-0.5C1.1,0,1.4,0,1.6,0.1l7.6,4.5c0.2,0.1,
    0.3,0.3,0.3,0.5c0,0.2-0.1,0.4-0.3,0.5L1.6,9.9z`;

const PLAY_BUTTON_DIAMETER = 121;
const PLAY_BUTTON_RADIUS = Math.floor(PLAY_BUTTON_DIAMETER / 2);

type LastSecondData = {
    user?: {
        contentItemProgresses?: Array<VideoItemProgress>;
    };
};

type LastSecondVariables = {
    readableVideoId: string;
};

const LAST_SECOND_QUERY: GqlOperation<LastSecondData, LastSecondVariables> =
    gqlOp(gql`
        query getLastSecond($readableVideoId: String!) {
            user {
                id
                contentItemProgresses(queryBy: {videoSlug: $readableVideoId}) {
                    ... on VideoItemProgress {
                        lastSecondWatched
                    }
                }
            }
        }
    `);

type SettingsData = {
    user?: {
        hasClasses: boolean;
        hasPermission: boolean;
        muteVideos: boolean;
        noColorInVideos: boolean;
        showCaptions: boolean;
        playbackRate: PlaybackRate;
    };
};

export const VIDEO_SETTINGS_QUERY: GqlOperation<SettingsData> = gqlOp(gql`
    query getVideoPlayerSettings {
        user {
            id
            hasClasses
            hasPermission(name: "can_moderate_users")
            muteVideos
            noColorInVideos
            showCaptions
            playbackRate
        }
    }
`);

const VideoPlayerPlaceholder = () => (
    <div className={`ka-video-player ${css(staticStyles.root)}`}>
        <div className="video-spacer-container">
            <div
                className={
                    "ka-video-player-container " +
                    css(
                        staticStyles.container,
                        staticStyles.centeredWhenWindowed,
                    )
                }
            >
                <div
                    data-testid="video-player-placeholder"
                    className={css(staticStyles.playButtonContainer)}
                >
                    <DeprecatedSpinner />
                </div>
            </div>
        </div>
    </div>
);

type VideoItemProgress = {
    lastSecondWatched: number;
};

type OwnProps = {
    // The time in seconds to start the video at, if possible.
    // If it isn't provided, we fetch it from the server using graphql.
    startTime: number;
    // The player might use custom styles or controls on certain pages.
    hidePlayButton?: boolean;
    // Event handlers for mouseenter and mouseleave on the container.
    onPlayerMouseEnter: () => unknown;
    onPlayerMouseLeave: () => unknown;
    // Function to call when component is fully interactive.
    onComponentInteractive: (event?: any) => unknown;
    // Whether the player is in an /embed_video frame.
    embedded?: boolean;
    // An HTML ID for the iframe to render.
    // TODO(riley): We shouldn't need this.
    iframeId: string;
    model: VideoPlayerModelType;
    onStateChange?: (event?: any) => unknown;
    // TODO(riley): This, as well as all properties of `model.player`,
    // should exist in a player-level Redux store.
    playerState: (typeof VideoPlayerState)[keyof typeof VideoPlayerState];
    // Some times when the video is in a modal we don't want the video
    // resizing.  This allows us to specify a fixed size.
    size?: Size;
    // Used as part of the `title` attribute for the rendered element.
    title: string;
    // TODO(kevinb): migrate to object-fit:cover with resize event listener
    inModal: boolean;
    // Whether or not the video should start automatically. Should not be used for content videos,
    // only for videos that include a still image or button that starts the play.
    // autoplay videos are an a11y no-go unless it is used when
    // a user action on another element triggers the video to load/play.
    // ex: a user clicks a button saying "watch video" and then the video loads and begins to autoplay.
    shouldAutoplay?: boolean;
};

type Size = {
    height: number;
    width: number;
};

type Props = OwnProps &
    WithUserProfileProps &
    WithLocationProps &
    WithApolloProps;

type DefaultProps = {
    startTime: Props["startTime"];
    hidePlayButton: Props["hidePlayButton"];
    onPlayerMouseEnter: Props["onPlayerMouseEnter"];
    onPlayerMouseLeave: Props["onPlayerMouseLeave"];
    onComponentInteractive: Props["onComponentInteractive"];
};

type State = {
    videoSettingsLoading: boolean;
    startTime: number;
    playerReady: boolean;
    isFocusable: boolean;
    isFullScreen: boolean;
    forceAutoplay: boolean;
    videoOffsetBottom?: number;
    togglePlayback: () => unknown | null | undefined;
    youTubeIsAvailable: boolean;
    supportsTouchEvents: boolean;
    useFallbackPlayer: boolean;
    spacerContainerHeight: number;
    // user video settings fetched from graphql
    hasClasses: boolean;
    isModerator: boolean;
    noColorInVideos: boolean;
    muteVideos: boolean;
    showCaptions: boolean;
    playbackRate: PlaybackRate;
};

class VideoPlayer extends React.Component<Props, State> {
    _mounted: boolean;
    _root: HTMLElement | null | undefined;
    _spacerContainer: HTMLElement | null | undefined;
    _player:
        | React.ElementRef<typeof YouTubePlayer>
        | null
        | undefined
        | HtmlPlayerWrapper
        | null
        | undefined;

    static defaultProps: DefaultProps = {
        startTime: 0,
        hidePlayButton: false,
        onPlayerMouseEnter: () => {},
        onPlayerMouseLeave: () => {},
        onComponentInteractive: () => {},
    };

    constructor(props: Props) {
        super(props);

        const {location} = props;

        this._mounted = false;

        this.state = {
            videoSettingsLoading: true,
            isFocusable: true,
            isFullScreen: false,
            playerReady: false,
            supportsTouchEvents: false,
            // @ts-expect-error - TS2322 - Type 'null' is not assignable to type '() => unknown'.
            togglePlayback: null,
            forceAutoplay: false,
            spacerContainerHeight: 0,

            // Enable the HTML player for folks who aren't blocked from youtube.
            // This gets updated on mount to also consider a cookie, but for
            // SSR, this is sufficient.
            useFallbackPlayer: isYouTubeBlocked(location),

            youTubeIsAvailable: false,
            startTime: this.getInitialStartTime(),
            // default settings values
            hasClasses: false,
            isModerator: false,
            noColorInVideos: false,
            muteVideos: false,
            showCaptions: true,
            playbackRate: "RATE_100_PERCENT",
        };
    }

    componentDidMount() {
        this._mounted = true;

        const {location, shouldAutoplay} = this.props;

        if (shouldUseFallbackPlayer(location)) {
            // NOTE: This calls setState internally.
            // Really shouldn't do that directly from componentDidMount.
            // TODO(somewhatabstract): Put these state changes into a timeout
            // using Wonder Blocks Timing, or just move these state updates
            // into the `then` after the less import below.
            this.setFallbackFlags();
        }

        addFullscreenChangeHandler(this.handleFullScreenChange);

        this.setState({
            supportsTouchEvents: supports.touchevents,
        });

        // HACK: if this page is eagerly loaded (e.g., because it is on a page
        // that could be rendered on the server and needs to be rehydrated),
        // it is possible that video.css is still being loaded. In that case,
        // _root could not be in the right position yet, in which case focusing
        // it would result in an unwanted scroll.
        import("../../../stylesheets/video-package/video.less").then(() => {
            if (shouldAutoplay && this._root && this._root.focus) {
                this._root.focus();
            }
        });

        // Ensure that we wait until componentDidMount to load the captions,
        // as it will perform a network request, which shouldn't happen until
        // mount occurs (especially during server-side render).
        this.fetchVideoSettings();
    }

    componentWillUnmount() {
        const {model} = this.props;

        // NOTE(jeresig): We need to pause the video when the user navigates
        // away to ensure that we don't try to keep logging progress on a video
        // that no longer exists.
        model.setPlayerState(VideoPlayerState.PAUSED);
        removeFullscreenChangeHandler(this.handleFullScreenChange);

        this._mounted = false;
    }

    getInitialStartTime(): number {
        const {location, profile, model} = this.props;

        // If the timestamp url param 't' has been set, we ignore user's
        // previous progress and skip to that timestamp.  This is primarily for
        // SEO.  If we provide a url pattern for skipping to a specific
        // timestamp in videos, Google can automatically display key moments in
        // searches. Having our videos displayed with key moments increases the
        // footprint of our search results, increasing clickthrough rate.
        const startTimeParam = parseInt(location.queryParams.t, 10);
        if (
            !isNaN(startTimeParam) &&
            startTimeParam >= 0 &&
            startTimeParam <= model.getDuration()
        ) {
            return startTimeParam;
        }

        // We access the userInfoStore directly here because createReactClass
        // React components don't work with redux-react's connect/Provider.
        const lastSecondWatched =
            userInfoStore.getState().lastSecondWatchedMap[
                model.get("readbleId")
            ];
        if (getKaid(profile)) {
            return lastSecondWatched;
        }

        // For users who aren't logged in -- they're not even a phantom
        // -- we know the start-time must be 0.
        return lastSecondWatched ?? 0;
    }

    fetchVideoSettings: () => void = () => {
        this.props.gqlClient
            .query(VIDEO_SETTINGS_QUERY, {
                fetchPolicy: "cache-first",
            })
            .then((response) => {
                this.setState({videoSettingsLoading: false});
                if (response.data?.user) {
                    this.setState({
                        hasClasses: response.data.user.hasClasses,
                        isModerator: response.data.user.hasPermission,
                        noColorInVideos: response.data.user.noColorInVideos,
                        muteVideos: response.data.user.muteVideos,
                        showCaptions: response.data.user.showCaptions,
                        playbackRate:
                            response.data.user.playbackRate ||
                            this.state.playbackRate,
                    });
                }
            });
    };

    updateMuteVideosSetting: (muteVideos: boolean) => void = (muteVideos) => {
        this.setState({muteVideos});
        updateMuteVideos(this.props.gqlClient, muteVideos);
    };

    updateShowCaptionsSetting: (showCaptions: boolean) => void = (
        showCaptions,
    ) => {
        updateShowCaptions(this.props.gqlClient, showCaptions);
    };

    updatePlaybackRateSetting: (playbackRate: PlaybackRate) => void = (
        playbackRate,
    ) => {
        updatePlaybackRate(this.props.gqlClient, playbackRate)?.then(
            (rate: unknown) => {
                // only update state if we get a value back
                if (rate != null) {
                    // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'PlaybackRate'.
                    this.setState({playbackRate: rate});
                }
            },
        );
    };

    setFallbackFlags() {
        const {model, location} = this.props;
        model.set("playerType", "html");

        markFallbackPlayerRequired();

        // Make sure we render with the fallback player.
        this.setState({
            useFallbackPlayer: true,
        });

        // Check if we should show the "Switch to full player" banner.
        if (!isYouTubeBlocked(location)) {
            requireYouTubeAPI(
                () =>
                    this._mounted && this.setState({youTubeIsAvailable: true}),
                () =>
                    // this doesn't log on fail because youtube-player
                    // will have logged it already
                    this._mounted && this.setState({youTubeIsAvailable: false}),
            );
        }
    }

    switchToFallbackPlayer: (options?: ErrorOptions) => void = ({
        fallbackPlayerIsTemporary,
        forceAutoplay,
    } = {}) => {
        if (this._mounted) {
            this.setState({
                useFallbackPlayer: true,
                // @ts-expect-error - TS2322 - Type 'boolean | undefined' is not assignable to type 'boolean'.
                forceAutoplay,
            });
            if (!fallbackPlayerIsTemporary) {
                this.setFallbackFlags();
            }
        }
    };

    switchToYouTubePlayer: () => void = () => {
        // HACK(mdr): It seems like it'll be _super_ rare that this control
        //     will ever actually appear for users, so, instead of investing in
        //     additional code complexity to smoothly switch the players, let's
        //     just build the suboptimal but super-future-proof UX of saving
        //     the new settings and reloading the page.
        markFallbackPlayerOptional();
        reload();
    };

    handleStateChange: (e: {data: VideoPlayerStateType}) => void = (e) => {
        const {data: newPlayerState} = e;
        const {onStateChange, model} = this.props;
        const {isFullScreen} = this.state;

        onStateChange && onStateChange(e);

        if (newPlayerState === VideoPlayerState.ENDED && isFullScreen) {
            exitFullscreen();
        }

        model.setPlayerState(newPlayerState);
    };

    handlePlayerReady: (togglePlayback: () => unknown) => void = (
        togglePlayback,
    ) => {
        const {onComponentInteractive} = this.props;
        const newState: Partial<State> = {};
        newState.playerReady = true;
        newState.togglePlayback = togglePlayback;
        onComponentInteractive();
        const spacerContainer = this._spacerContainer;

        if (spacerContainer) {
            newState.spacerContainerHeight = spacerContainer.clientHeight;
            newState.videoOffsetBottom =
                newState.spacerContainerHeight +
                spacerContainer.getBoundingClientRect().top +
                (document.body?.scrollTop ?? 0);
        }

        if (this._mounted) {
            // @ts-expect-error - TS2345 - Argument of type 'Partial<State>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
            this.setState(newState);
        }
    };

    handlePlayButtonClick = (e: React.MouseEvent) => {
        e.preventDefault();
        this.props.model?.player?.playVideo();
        // a11y: Focus the player (if it exists) so as to not drop focus when
        // the play button we just clicked is removed from the DOM.
        if (this._player) {
            // ReactDOM.findDOMNode can return null, Text, or Element
            // We case the result to any so that we can .focus() on it
            // and then double check that it's defined and actually has
            // that method defined. This should focus the youtube player's
            // iframe when our custom button is clicked.  This is really
            // just to make TypeScript happy because we've already checked that
            // this._player exists above.
            // TODO(kevinb): use ref forwarding so that we don't have to
            // use findDOMNode which is being deprecated in the future.
            const element = findDOMNode(this._player as any) as any;
            if (element?.focus) {
                element.focus();
            }
        }
    };

    handleFullScreenChange: () => void = () => {
        if (this._mounted) {
            this.setState({isFullScreen: !this.state.isFullScreen});
        }
    };

    handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void = (e) => {
        if (e.keyCode === 32) {
            e.preventDefault(); // Cancel scrolling with the spacebar
            if (this.state.togglePlayback) {
                this.state.togglePlayback();
            }
        }
    };

    handleBlur: () => void = () => {
        if (this._mounted) {
            this.setState({isFocusable: false});
        }
    };

    getStartTime(data?: LastSecondData | null): number {
        const progresses =
            (data && data.user && data.user.contentItemProgresses) || [];

        return (
            (progresses.length === 1 && progresses[0].lastSecondWatched) || 0
        );
    }

    renderPlayButton(showSwitchToYouTubePlayerLink: boolean): React.ReactNode {
        const {inModal} = this.props;
        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
            <div
                className={css(
                    staticStyles.playButtonContainer,
                    inModal &&
                        showSwitchToYouTubePlayerLink &&
                        staticStyles.playButtonContainerYouTubeLinkShowing,
                )}
                onClick={this.handlePlayButtonClick}
            >
                <button
                    aria-label={t`Play video`}
                    className={css(staticStyles.playButton)}
                    onClick={this.handlePlayButtonClick}
                    data-testid="video-play-button"
                >
                    <svg
                        height={PLAY_BUTTON_DIAMETER}
                        width={PLAY_BUTTON_DIAMETER}
                    >
                        <circle
                            cx={PLAY_BUTTON_RADIUS}
                            cy={PLAY_BUTTON_RADIUS}
                            r={PLAY_BUTTON_RADIUS}
                            stroke={color.white}
                            strokeWidth="1"
                            fill={color.blue}
                        />
                        <path
                            d={playIcon}
                            fill={color.white}
                            transform="
                                            scale(4.375, 4.375)
                                            translate(9.75, 8.25)
                                        "
                        />
                    </svg>
                </button>
            </div>
        );
    }

    renderPlayer(startTime: number): React.ReactNode {
        const {
            isFocusable,
            playerReady,
            supportsTouchEvents,
            useFallbackPlayer,
            youTubeIsAvailable,
            forceAutoplay,
            hasClasses,
            isModerator,
            noColorInVideos,
            muteVideos,
            showCaptions,
            playbackRate,
        } = this.state;

        const {
            embedded,
            hidePlayButton,
            iframeId,
            inModal,
            model,
            onPlayerMouseEnter,
            onPlayerMouseLeave,
            playerState,
            size,
            title,
            shouldAutoplay,
        } = this.props;

        const mouseHandlers = {
            onMouseEnter: onPlayerMouseEnter,
            onMouseLeave: onPlayerMouseLeave,
        } as const;

        // TODO(riley): Handle flicker from YouTube video state.
        const showPlayButton =
            playerReady &&
            isUnstartedOrCued(playerState) &&
            !(hidePlayButton || supportsTouchEvents);

        const showSwitchToYouTubePlayerLink =
            useFallbackPlayer && youTubeIsAvailable;

        const playerProps = {
            model,
            muteVideos: embedded === true && isInApp() ? false : muteVideos,
            showCaptions,
            playbackRate,
            autoplay: shouldAutoplay,
            onPlayerReady: this.handlePlayerReady,
            onStateChange: this.handleStateChange,
            // The YouTube player ignores fractional start times.
            startTime: Math.round(startTime),
            styles: playerStyles,
            tabAccessible: !showPlayButton,
            title: t`Video: ${title}`,
            updateMuteVideosSetting: this.updateMuteVideosSetting,
            updateShowCaptionsSetting: this.updateShowCaptionsSetting,
            updatePlaybackRateSetting: this.updatePlaybackRateSetting,
        } as const;

        if (forceAutoplay) {
            // @ts-expect-error - TS2540 - Cannot assign to 'autoplay' because it is a read-only property.
            playerProps.autoplay = true;
        }

        const cachedClarifications = !isModerator;

        const playerRef = (
            node:
                | React.ElementRef<typeof YouTubePlayer>
                | null
                | undefined
                | HtmlPlayerWrapper
                | null
                | undefined,
        ) => (this._player = node);

        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
            <div
                className={this.playerRootClasses(hasClasses)}
                tabIndex={isFocusable ? -1 : undefined}
                onKeyDown={this.handleKeyDown}
                ref={(ref) => (this._root = ref)}
            >
                <span className="sr-only" data-testid="video-time-hidden">
                    {t`Current time:`}
                    {formatTimeDisplay(model.getVideoPosition(0, true))}
                    {t`Total duration:`}
                    {formatTimeDisplay(model.getDuration())}
                </span>
                <div
                    className={
                        "video-spacer-container " +
                        css(staticStyles.spacerContainer)
                    }
                    ref={(el) => (this._spacerContainer = el)}
                    style={this.getSpacerContainerStyle(size)}
                >
                    <div
                        className={
                            "ka-video-player-container " +
                            css(
                                staticStyles.container,
                                staticStyles.centeredWhenWindowed,
                                noColorInVideos && staticStyles.noColorInVideos,
                            )
                        }
                        {...mouseHandlers}
                    >
                        {useFallbackPlayer ? (
                            <HtmlPlayerWrapper
                                {...playerProps}
                                ref={playerRef}
                            />
                        ) : (
                            <YouTubePlayer
                                id={iframeId}
                                onError={this.switchToFallbackPlayer}
                                size={size}
                                {...playerProps}
                                ref={playerRef}
                            />
                        )}
                    </div>

                    <div className={css(staticStyles.clarifications)}>
                        <ClarificationNotifications
                            cached={cachedClarifications}
                            videoModel={model}
                        />
                    </div>

                    {showPlayButton &&
                        this.renderPlayButton(showSwitchToYouTubePlayerLink)}
                </div>
                {/* TODO(riley): Move to `ka-video-wrapper.jsx`. */}
                {showSwitchToYouTubePlayerLink && (
                    <div
                        className={css(
                            staticStyles.switchToYouTubePlayerBanner,
                            inModal &&
                                staticStyles.switchToYouTubePlayerBannerModal,
                        )}
                    >
                        <a
                            href="javascript:void 0"
                            onClick={this.switchToYouTubePlayer}
                            className={css(
                                staticStyles.switchToYouTubePlayerLink,
                            )}
                        >
                            {t`Switch to full player`}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    getReadableId(): string {
        // FrozenVideos use readableId but ContentListItems use slug.
        // TODO(kevinb): update player model use id instead.
        return (
            this.props.model.get("readableId") || this.props.model.get("slug")
        );
    }

    updateUserInfoStore: (data?: LastSecondData | null | undefined) => void = (
        data,
    ) => {
        const readableId = this.getReadableId();
        const startTime = this.getStartTime(data);

        const action: Action = {
            type: "UPDATE_LAST_SECOND_WATCHED",
            readableId,
            lastSecondWatched: startTime,
        };

        userInfoStore.dispatch(action);
    };

    playerRootClasses(showAssignmentsHeader?: boolean | null): string {
        const {isFullScreen} = this.state;
        const {embedded, inModal, playerState, size} = this.props;
        const videoEnded = playerState === VideoPlayerState.ENDED;

        // TODO(riley): Remove these CSS classes.
        return classNames(
            {
                fullscreen: isFullScreen,
                "video-ended": videoEnded,
                "ka-video-player": !size,
                // Avoid applying the root width constraints if we've been provided
                // with an explicit size.
            },
            css(
                !size && staticStyles.root,
                !size && embedded && staticStyles.embedRoot,
                !size && inModal && staticStyles.modalRoot,
                !size &&
                    inModal &&
                    showAssignmentsHeader &&
                    staticStyles.modalRootWithAssignments,
                videoEnded && staticStyles.rootWhenVideoEnded,
                staticStyles.noOutline,
            ),
        );
    }

    getSpacerContainerStyle(size?: Size | null): {
        height: number;
        width?: number;
    } {
        // We set the height of the video container so that when we show the
        // header bar and move the video element into it, the page height
        // doesn't suddenly shrink and cause jumpiness.
        const {spacerContainerHeight} = this.state;
        return size
            ? {
                  width: size.width,
                  height: size.height,
              }
            : {
                  height: spacerContainerHeight,
              };
    }

    render(): React.ReactNode {
        const {startTime, videoSettingsLoading} = this.state;

        // wait for the video settings to load before rendering the player
        if (videoSettingsLoading) {
            return <VideoPlayerPlaceholder />;
        }

        // startTime is set only once in getInitialState() where we get
        // the lastSecondWatched for this video's readableId.  If it's an
        // actual value, i.e. a number, we use it as the starting time.
        // Otherwise, we fetch the value from the server via GraphQL.
        if (typeof startTime === "number") {
            return this.renderPlayer(startTime);
        }
        const readableId = this.getReadableId();
        return (
            <ApolloQuery
                query={LAST_SECOND_QUERY}
                variables={{
                    readableVideoId: readableId,
                }}
                fetchPolicy="cache-and-network"
                skip={!PermissionsInfo.hasProfile}
                onCompleted={this.updateUserInfoStore}
            >
                {({data, loading}) => {
                    if (loading) {
                        return <VideoPlayerPlaceholder />;
                    }

                    const startTime = this.getStartTime(data);

                    return this.renderPlayer(startTime);
                }}
            </ApolloQuery>
        );
    }
}

// Let's make our video player fluid on two different axes. The design spec
// currently calls for the following behavior:
//
// 1. A constant 16:9 aspect ratio
// 2. 100% width until 1280px, at which point the video should be centered
// 3. At most 60% of the viewport height
//
// We solve constraints (2) and (3) using *two* container elements. One is
// located in the following `.ka-video-player` rule, the other can be found
// in Aphrodite on the <VideoPage> component.
//
// TODO(mdr): These styles were copied from video-page.less, which, despite the
//     name, applies to all of our video player call sites. The styles from
//     ka-video-player.less should be merged into here, too, at which point the
//     many classnames scattered around this component can be removed.
// TODO(mdr): *Should* these auto-scaling styles apply to all of our call
//     sites? I definitely want this behavior for the SAT video modal as well
//     as the video page, but it might not be appropriate for all future use
//     cases.
const aspectRatio = 16 / 9;
const modalHeightPercentage = 90;

// TODO(benkomalo): move these fudge factors to shared constants.
const modalHeaderHeight = 60;
const modalFooterHeight = 60;
const modalChromeHeight = modalHeaderHeight + modalFooterHeight;

// NOTE(kevinb): This is based on the styles from video-modal.jsx which
// sets the modal to occupy 90% of the screen and has a header and
// footer which occupy 60px of height each.
const genWidthCalc = (fudgeFactor: number) => {
    return (
        `calc(${aspectRatio * modalHeightPercentage}vh - ` +
        `${aspectRatio * fudgeFactor}px)`
    );
};

const staticStyles = StyleSheet.create({
    root: {
        margin: "auto",
        maxWidth: "100%",
        position: "relative",

        // Since our aspect ratio is constant, we can place constraint (3) on
        // the width of the container - based on the viewport height.
        width: `${aspectRatio * 60}vh`,
    },

    embedRoot: {
        width: `${aspectRatio * 100}vh`,
    },

    modalRoot: {
        width: genWidthCalc(modalChromeHeight),
        [mediaQueries.smOrSmaller]: {
            width: "100%",
        },
    },

    modalRootWithAssignments: {
        // HACK(benkomalo): blurgggghh. Same as modalRoot, there's an implicit
        // assumption here about what else is showing in the page when the
        // modal is up. In the case where we're showing an assignments, header,
        // though, we not only have the modal header and footer, we have
        // an assignments header which pushes everything down.
        width: genWidthCalc(modalChromeHeight + topBarHeightDesktop),
        [mediaQueries.smOrSmaller]: {
            width: "100%",
        },
    },

    rootWhenVideoEnded: {
        // TODO(mdr): Is this just to override the padding-bottom for
        //     .video-ended in ka-video-player.less?
        paddingBottom: 0,
    },

    container: {
        // Fix the aspect ratio to 16:9.
        paddingBottom: `${(9 / 16) * 100}%`,
        position: "relative",
        width: "100%",
    },

    spacerContainer: {
        // TODO(mdr): Is this just to override the embedded and fullscreen
        //     .video-spacer-container height in ka-video-player.less?
        //     Or is it to override the style value that we set right here in
        //     this component? Suspicious.
        height: "auto !important",
    },

    playButtonContainer: {
        alignItems: "center",
        bottom: 0,
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
    },

    playButtonContainerYouTubeLinkShowing: {
        // Only needed when the link is inside the video-player container
        [mediaQueries.mdOrLarger]: {
            bottom: 38,
        },
    },

    playButton: {
        border: "1px solid white",
        borderRadius: "50%",
        boxShadow: "0 0 1px 1px white",
        cursor: "pointer",
        height: PLAY_BUTTON_DIAMETER,
        width: PLAY_BUTTON_DIAMETER,
        padding: 0,
    },

    video: {
        height: "100%",
        left: 0,
        position: "absolute",
        top: 0,
        width: "100%",
    },

    noColorInVideos: {
        filter: "grayscale()",
    },

    centeredWhenWindowed: {
        // TODO(mdr): I don't understand what the "when windowed" condition
        //     means here; I just copied the name from an old stylesheet.
        display: "block",
        margin: "auto",
        // TODO(mdr): Is this just to override the max-width for
        //     .centered-when-windowed in ka-video-player.less?
        maxWidth: "none",
    },

    clarifications: {
        margin: "0 auto",
    },

    switchToYouTubePlayerBanner: {
        bottom: -28,
        fontSize: 16,
        position: "absolute",
        right: 8,
        textAlign: "right",
        zIndex: 1,
    },

    switchToYouTubePlayerBannerModal: {
        // In a modal, when the screen is large, we do not use a full-screen
        // modal and thus do not have room below the video player for this
        // container to display. This moves the link inside the video player
        // container instead of outside it's bounds.
        [mediaQueries.mdOrLarger]: {
            position: "static",
            paddingRight: 16,
            paddingBottom: 8,
            paddingTop: 8,
            background: color.white,
        },
    },

    switchToYouTubePlayerLink: {
        textDecoration: "none",
        color: color.blue,

        ":hover": {
            textDecoration: "underline",
        },
    },

    noOutline: {
        outline: "none",
    },
});

const playerStyles = [staticStyles.video, staticStyles.centeredWhenWindowed];

type ExportProps = WithoutLocation<
    WithoutApollo<
        WithoutUserProfile<
            JSX.LibraryManagedAttributes<
                typeof VideoPlayer,
                React.ComponentProps<typeof VideoPlayer>
            >
        >
    >
>;

export default compose(
    withUserProfile,
    withLocation,
    withBackboneModels(({model}: Props) => [model]),
    withApollo,
)(VideoPlayer) as React.ComponentType<ExportProps>;
